import { useState, FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import { api, ApiClientError } from '@/api/client';
import type { TenantFormData } from '@/types';

type Mode = 'create' | 'edit';

export function TenantForm() {
  const { id } = useParams<{ id?: string }>();
  const mode: Mode = id ? 'edit' : 'create';
  const navigate = useNavigate();

  const [form, setForm] = useState<TenantFormData>({
    tenant_id: '',
    name: '',
    max_users: 50,
    max_requests_per_day: 1000,
    max_requests_per_month: 20000,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // For edit mode, load existing data
  const [loaded, setLoaded] = useState(mode === 'create');
  if (!loaded && id) {
    api.getTenant(id).then((t) => {
      setForm({
        tenant_id: t.tenant_id,
        name: t.name,
        max_users: t.max_users,
        max_requests_per_day: t.max_requests_per_day,
        max_requests_per_month: t.max_requests_per_month,
      });
      setLoaded(true);
    });
  }

  function set<K extends keyof TenantFormData>(k: K, v: TenantFormData[K]) {
    setForm((prev) => ({ ...prev, [k]: v }));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      if (mode === 'create') {
        await api.createTenant(form);
      } else {
        await api.updateTenant(id!, {
          name: form.name,
          max_users: form.max_users,
          max_requests_per_day: form.max_requests_per_day,
          max_requests_per_month: form.max_requests_per_month,
        });
      }
      navigate('/tenants');
    } catch (e) {
      setError(e instanceof ApiClientError ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  if (!loaded) {
    return <div className="text-sm text-gray-500 dark:text-gray-400 py-12 text-center">Loading…</div>;
  }

  return (
    <div className="max-w-2xl">
      <button
        onClick={() => navigate('/tenants')}
        className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 mb-6"
      >
        <ArrowLeft size={15} /> Tenants
      </button>

      <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6">
        {mode === 'create' ? 'Add Tenant' : `Edit — ${form.name}`}
      </h2>

      <form onSubmit={handleSubmit} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 space-y-5">
        <div>
          <label className="label">Tenant ID <span className="text-red-500">*</span></label>
          <input
            className="input font-mono"
            placeholder="e.g. titan"
            value={form.tenant_id}
            onChange={(e) => set('tenant_id', e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))}
            disabled={mode === 'edit'}
            required
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Lowercase letters, digits, hyphens and underscores. Cannot be changed after creation.
          </p>
        </div>

        <div>
          <label className="label">Organisation Name <span className="text-red-500">*</span></label>
          <input
            className="input"
            placeholder="e.g. Titan Hospitals"
            value={form.name}
            onChange={(e) => set('name', e.target.value)}
            required
          />
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="label">Max Users</label>
            <input
              type="number"
              className="input"
              min={1}
              value={form.max_users}
              onChange={(e) => set('max_users', Math.max(1, parseInt(e.target.value) || 1))}
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Concurrent widget users</p>
          </div>
          <div>
            <label className="label">Max Requests / Day</label>
            <input
              type="number"
              className="input"
              min={1}
              value={form.max_requests_per_day}
              onChange={(e) => set('max_requests_per_day', Math.max(1, parseInt(e.target.value) || 1))}
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Splits evenly across active users
            </p>
          </div>
          <div>
            <label className="label">Max Requests / Month</label>
            <input
              type="number"
              className="input"
              min={1}
              value={form.max_requests_per_month}
              onChange={(e) => set('max_requests_per_month', Math.max(1, parseInt(e.target.value) || 1))}
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Hard monthly cap for tenant</p>
          </div>
        </div>

        <div className="bg-brand-50 dark:bg-brand-900/30 rounded-lg p-4 text-sm text-brand-800 dark:text-brand-300">
          <div className="font-semibold mb-1">Quota per user</div>
          <div>
            Each active user will receive{' '}
            <strong>
              {Math.floor(form.max_requests_per_day / Math.max(1, form.max_users)).toLocaleString()}
            </strong>{' '}
            requests/day (based on max users). Actual quota adjusts as users join.
          </div>
        </div>

        {error && (
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        )}

        <div className="flex gap-3 pt-2">
          <button type="submit" disabled={saving} className="btn-primary">
            <Save size={15} className="mr-2" />
            {saving ? 'Saving…' : mode === 'create' ? 'Create Tenant' : 'Save Changes'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/tenants')}
            className="btn-secondary"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
